import React, { useState, useEffect } from 'react';
import { AppLayout } from '../../components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  MapPin, 
  Search, 
  Phone, 
  Clock,
  Navigation,
  Filter
} from 'lucide-react';

export const AgentDiscoveryPage = () => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/agent-network/browse`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setAgents(data.agents || []);
      }
    } catch (error) {
      console.error('Failed to fetch agents:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAgents = agents.filter(agent =>
    agent.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.area?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AppLayout>
      <div className="p-4 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Find Agents</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Locate nearby agents for cash services
          </p>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            type="text"
            placeholder="Search by location or business name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Agent List */}
        <div className="space-y-3">
          {loading ? (
            <Card>
              <CardContent className="p-6 text-center text-gray-600 dark:text-gray-400">
                Loading agents...
              </CardContent>
            </Card>
          ) : filteredAgents.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center">
                <MapPin className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                <p className="text-gray-600 dark:text-gray-400">
                  {searchTerm ? 'No agents found matching your search' : 'No agents available in your area yet'}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredAgents.map((agent) => (
              <Card key={agent.agent_id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                        {agent.business_name || 'Agent'}
                      </h3>
                      <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          <span>{agent.area}, {agent.city}</span>
                        </div>
                        {agent.phone_number && (
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4" />
                            <span>{agent.phone_number}</span>
                          </div>
                        )}
                        {agent.operating_hours && (
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            <span>{agent.operating_hours}</span>
                          </div>
                        )}
                      </div>
                      {agent.services_offered && agent.services_offered.length > 0 && (
                        <div className="flex gap-2 mt-2">
                          {agent.services_offered.map((service, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs rounded-full"
                            >
                              {service}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button size="sm" variant="outline">
                      <Navigation className="h-4 w-4 mr-2" />
                      Directions
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </AppLayout>
  );
};
